import { useCallback } from "react";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { Track, Waypoint } from "types/app";
import { encodeGPX, GPXTrack } from "utils/gpx/encodeGPX";
import { getSafeName } from "utils/gpx/getExportSafeName";
import { showTrackExportSuccessNotification } from "utils/notifications/trackNotifications";
import { showWaypointExportSuccessNotification } from "utils/notifications/waypointNotifications";
import { useGenericModals } from "./useGenericModals";

/** The GPX data of a track: its saved data, or `trackPoints` / `trackPointsEle` of the edited one */
export const toGPXTrack = (
  track: Partial<
    Pick<Track, "name" | "desc" | "links" | "cmt" | "number" | "type" | "src">
  >,
  points: Pick<GPXTrack, "trackPoints" | "trackPointsEle">
): GPXTrack => ({
  name: track.name,
  description: track.desc,
  links: track.links,
  comment: track.cmt,
  number: track.number,
  type: track.type,
  source: track.src,
  ...points,
});

/**
 * Downloads tracks or waypoints as a GPX file: named after the only item, "export.gpx" for more. Tells the user that
 * it's done or that it failed, returns whether it was downloaded.
 */
export const useExportGPX = () => {
  const { openErrorModal } = useGenericModals();
  const { t } = useTranslation();

  return useCallback(
    ({
      tracks = [],
      waypoints = [],
    }: {
      tracks?: GPXTrack[];
      waypoints?: Waypoint[];
    }) => {
      const items = [...tracks, ...waypoints];
      try {
        const encoded = encodeGPX({ tracks, waypoints });
        if (!encoded || items.length === 0)
          throw new Error("Nothing to export");

        const blob = new Blob([encoded], { type: "application/gpx+xml" });
        const name = items.length === 1 ? items[0].name : undefined;
        saveAs(
          blob,
          items.length === 1 ? `${getSafeName(name)}.gpx` : "export.gpx"
        );

        // A single item without a name is counted ("1 track exported")
        const notified = name ? { name } : { count: items.length };
        if (waypoints.length > 0 && tracks.length === 0) {
          showWaypointExportSuccessNotification(notified);
        } else {
          showTrackExportSuccessNotification(notified);
        }
        return true;
      } catch (_error) {
        openErrorModal({
          title: t("generic.error_title"),
          text: t("errors.encoding_GPX_failed.text"),
        });
        return false;
      }
    },
    [openErrorModal, t]
  );
};
